/**
 * <hero-slideshow>
 *
 * Companion element for sections/hero-slideshow.liquid.
 *
 * assets/slideshow.js handles scroll-snap, drag, autoplay, infinite loop, keyboard and ARIA
 * for the main slideshow. It has zero <video> awareness, so this element owns:
 *
 *   1. Video lifecycle — play only the active slide, and defer source loading until first
 *      activation. Without this, every video slide downloads and decodes at once, forever.
 *      (Deferral pattern from assets/video-background.js; play/pause precedent at
 *      assets/layered-slideshow.js:309.)
 *   2. Reduced motion — slideshow.js consults prefersReducedMotion() only for scroll
 *      smoothness, never for autoplay, so rotation is paused here explicitly.
 *   3. The stats strip below the hero is a pure-CSS marquee — no JS involved.
 *
 * Deliberately import-free: a plain HTMLElement that cannot break if the importmap changes.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = matchMedia('(max-width: 749px)');

class HeroSlideshow extends HTMLElement {
  #slides = [];

  connectedCallback() {
    this.slideshow = this.querySelector('slideshow-component');
    this.#slides = Array.from(this.querySelectorAll('slideshow-slide'));

    this.addEventListener('slideshow:select', this.#onSelect);

    // slideshow.js emits no event for the initial slide, so prime it manually.
    this.#sync(0);

    if (REDUCED.matches) this.#pauseRotation();
  }

  disconnectedCallback() {
    this.removeEventListener('slideshow:select', this.#onSelect);
  }

  /* ── Main slideshow: video lifecycle ─────────────── */

  #onSelect = (event) => {
    const index = event?.detail?.index;
    if (typeof index === 'number') this.#sync(index);
  };

  /** slideshow-component may not have upgraded yet when we connect. */
  #pauseRotation() {
    const stop = () => {
      try {
        this.slideshow?.pause?.();
      } catch {
        /* best-effort; a failure here must not break the slideshow */
      }
    };

    if (this.slideshow && !customElements.get('slideshow-component')) {
      customElements.whenDefined('slideshow-component').then(stop);
    } else {
      stop();
    }
  }

  #sync(activeIndex) {
    // CSS hides video on mobile and under reduced motion — never spend the bytes.
    if (REDUCED.matches || MOBILE.matches) return;

    this.#slides.forEach((slide, i) => {
      const video = slide.querySelector('[data-hsl-video]');
      if (!video) return;

      if (i === activeIndex) {
        this.#attach(video);
        // Autoplay rejection (no gesture, low power mode) is expected and non-fatal;
        // the poster image stays visible underneath.
        video.play?.().catch(() => {});
      } else {
        video.pause?.();
      }
    });
  }

  /** Copy data-hsl-src -> src on first activation, then load. */
  #attach(video) {
    if (video.dataset.hslLoaded) return;
    video.dataset.hslLoaded = '1';

    let attached = false;
    for (const source of video.querySelectorAll('source[data-hsl-src]')) {
      source.src = source.dataset.hslSrc;
      attached = true;
    }
    if (attached) video.load();
  }

}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
